/**
 * Emergency Booking Service
 * Manages emergency booking requests with Firebase for real-time cross-device sync
 */

import { PORTAL_URL } from '../config/urls';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  getDocs,
  getDoc,
  getDocsFromServer,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { hierarchicalServiceDataService } from './hierarchicalServiceDataService';
import { clientMayEmergencyBookService } from '../shared/emergency';
import { buildCounterOfferBody } from './smsService';
import { communicationService } from './communicationService';

interface EmergencyBookingRequest {
  id: string;
  clientId: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  requestedDate: Date;
  requestedTimeSlot: 'morning' | 'afternoon' | 'evening' | 'asap';
  serviceType: string;
  urgencyLevel: 'high' | 'critical';
  notes: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'counter_offered' | 'alternative_accepted';
  createdAt: Date;
  respondedAt?: Date;
  respondedBy?: string;
  assignedBarber?: string;
  assignedTime?: Date;
  rejectionReason?: string;
  alternativeSlots?: Array<{
    date: Date;
    time: string;
    barber: string;
    confidence: number;
  }>;
  // New: Proposed alternative from barber
  proposedAlternative?: {
    date: Date;
    time: string;
    barber: string;
    barberId?: string;
    message?: string;
    proposedAt: Date;
  };
  automaticallyApproved: boolean;
  emergencyFee: number;
}

interface EmergencyNotification {
  id: string;
  type: 'emergency_booking';
  bookingId: string;
  clientName: string;
  urgencyLevel: 'high' | 'critical';
  createdAt: Date;
  read: boolean;
}

interface EmergencyBookingStats {
  pendingCount: number;
  todayCount: number;
  thisWeekCount: number;
  approvalRate: number;
}

class EmergencyBookingService {
  // ✅ FIXED: Using correct collection name to match customer portal and Firestore rules
  private readonly BOOKINGS_COLLECTION = 'emergencyBookings';
  private subscribers: Array<(bookings: EmergencyBookingRequest[]) => void> = [];
  private notificationSubscribers: Array<(notifications: EmergencyNotification[]) => void> = [];
  private unsubscribeBookings: (() => void) | null = null;
  private cachedBookings: EmergencyBookingRequest[] = [];
  // Track which bookings have been "seen" by staff (for notification popup)
  private seenBookingIds: Set<string> = new Set();
  // H4 FIX (Jose 2026-10-29 audit): seen-state was in-memory ONLY, so a page refresh re-instantiated
  // the singleton with an empty Set and every already-dismissed emergency booking re-popped as "new".
  // Persist it so a dismissal sticks across reloads/re-subscribes.
  private static readonly SEEN_STORAGE_KEY = 'slik_emergency_seen_ids';

  constructor() {
    this.loadSeenFromStorage();
    // Initialize real-time listeners
    this.initializeListeners();
    // RESILIENCE (Jose 2026-10-29 audit): this singleton subscribes at module load, which can
    // happen BEFORE Firebase auth is ready → a pre-auth denied read leaves the list empty with no
    // self-heal. Re-subscribe once auth is ready (unsub prior first). See shiftService fix.
    try { onAuthStateChanged(auth, (u) => { if (u) this.initializeListeners(); }); } catch { /* ignore */ }
  }

  /**
   * Initialize Firebase real-time listeners
   * ✅ FIXED: No separate notifications collection - derive from bookings
   */
  private initializeListeners(): void {
    // Listen to emergency bookings
    try {
      // Tear down any prior listener first so an auth-ready re-subscribe never stacks duplicates.
      try { this.unsubscribeBookings?.(); } catch { /* ignore */ }
      this.unsubscribeBookings = null;

      const bookingsQuery = query(
        collection(db, this.BOOKINGS_COLLECTION),
        orderBy('createdAt', 'desc')
      );

      this.unsubscribeBookings = onSnapshot(bookingsQuery, (snapshot) => {
        const previousBookingIds = new Set(this.cachedBookings.map(b => b.id));

        this.cachedBookings = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          requestedDate: doc.data().requestedDate?.toDate() || new Date(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          respondedAt: doc.data().respondedAt?.toDate(),
          assignedTime: doc.data().assignedTime?.toDate(),
          alternativeSlots: doc.data().alternativeSlots?.map((slot: any) => ({
            ...slot,
            date: slot.date?.toDate() || new Date()
          }))
        })) as EmergencyBookingRequest[];

        // Check for NEW bookings (not seen before)
        const newBookings = this.cachedBookings.filter(b =>
          !previousBookingIds.has(b.id) && !this.seenBookingIds.has(b.id)
        );

        if (newBookings.length > 0) {
          console.log('🚨 New emergency booking(s) detected:', newBookings.map(b => b.clientName));
        }

        // Notify all subscribers
        this.notifySubscribers();

        // Also notify notification subscribers with derived notifications
        this.notifyNotificationSubscribers();
      }, (error) => {
        console.error('Error listening to emergency bookings:', error);
      });

    } catch (error) {
      console.error('Error initializing Firebase listeners:', error);
    }
  }

  /**
   * Notify notification subscribers with derived notifications from bookings
   */
  private notifyNotificationSubscribers(): void {
    // Generate notifications from unseen bookings
    const notifications: EmergencyNotification[] = this.cachedBookings
      // Only PENDING bookings are "new requests" worth popping — approved/rejected/completed/countered
      // are already triaged and must NEVER re-surface as an urgent popup. (2026-10-07: an approved test
      // booking was re-popping on every refresh.)
      .filter(b => b.status === 'pending' && !this.seenBookingIds.has(b.id))
      .map(booking => ({
        id: `notif-${booking.id}`,
        type: 'emergency_booking' as const,
        bookingId: booking.id,
        clientName: booking.clientName,
        urgencyLevel: booking.urgencyLevel,
        createdAt: booking.createdAt,
        read: false
      }));

    this.notificationSubscribers.forEach(cb => cb(notifications));
  }

  /**
   * Create a new emergency booking request
   */
  async createBooking(data: {
    clientId: string;
    clientName: string;
    clientPhone: string;
    clientEmail: string;
    requestedDate: Date;
    requestedTimeSlot: 'morning' | 'afternoon' | 'evening' | 'asap';
    serviceType: string;
    urgencyLevel: 'high' | 'critical';
    notes: string;
  }): Promise<EmergencyBookingRequest> {
    // EMERGENCY RESTRICTED-SERVICE GATE (task #46 Stage 3, Jose 2026-10-02): a service marked restricted
    // (allowEmergencySqueeze:false — fades/shaves, set per-service in Service Management) can only be
    // emergency-squeezed by a client GRANTED access for it in their profile. Fail-open on a lookup glitch
    // so a real emergency is never lost to a hiccup. Non-restricted services + granted clients are unaffected.
    let blockedReason: string | null = null;
    try {
      const service = await hierarchicalServiceDataService.findServiceByName(data.serviceType);
      if (service && service.allowEmergencySqueeze === false) {
        const clientSnap = await getDoc(doc(db, 'clients', data.clientId));
        const client = clientSnap.exists() ? (clientSnap.data() as any) : null;
        if (!clientMayEmergencyBookService(client, service)) {
          blockedReason = `"${data.serviceType}" needs emergency permission for this client — grant it in their profile first.`;
        }
      }
    } catch (e) {
      console.warn('[emergency] restricted-service gate lookup failed, allowing:', (e as any)?.message);
    }
    if (blockedReason) throw new Error(blockedReason);

    // Get configurable emergency fees from shop settings
    let emergencyFeeHigh = 37.50;
    let emergencyFeeCritical = 50;
    try {
      const shopSettingsDoc = await getDoc(doc(db, 'settings', 'shop'));
      if (shopSettingsDoc.exists()) {
        const fees = shopSettingsDoc.data().emergencyBookingSettings?.urgencyFees;
        if (fees) {
          emergencyFeeHigh = fees.urgent ?? fees.standard ?? 37.50;
          emergencyFeeCritical = fees.critical ?? 50;
        }
      }
    } catch {
      // Use defaults on error
    }
    const emergencyFee = data.urgencyLevel === 'critical' ? emergencyFeeCritical : emergencyFeeHigh;

    const bookingData = {
      ...data,
      requestedDate: Timestamp.fromDate(data.requestedDate),
      status: 'pending',
      createdAt: serverTimestamp(),
      automaticallyApproved: false,
      emergencyFee
    };

    try {
      // Add to Firebase
      const docRef = await addDoc(collection(db, this.BOOKINGS_COLLECTION), bookingData);

      const newBooking: EmergencyBookingRequest = {
        id: docRef.id,
        ...data,
        status: 'pending',
        createdAt: new Date(),
        automaticallyApproved: false,
        emergencyFee
      };

      // Create notification for staff
      await this.createStaffNotification(newBooking);

      console.log('🚨 Emergency booking created in Firebase:', docRef.id);
      return newBooking;

    } catch (error) {
      console.error('Error creating emergency booking:', error);
      throw error;
    }
  }

  /**
   * ✅ SIMPLIFIED: No separate notifications collection needed
   * Notifications are derived from bookings in real-time
   */
  private async createStaffNotification(booking: EmergencyBookingRequest): Promise<void> {
    // No separate notification needed - the popup component listens to bookings directly
    console.log('🔔 Emergency booking will trigger popup notification:', booking.clientName);
  }

  /**
   * Get all emergency bookings (from cache, updated in real-time)
   */
  getAllBookings(): EmergencyBookingRequest[] {
    return this.cachedBookings;
  }

  /**
   * Get all emergency bookings (alias)
   */
  getAll(): EmergencyBookingRequest[] {
    return this.getAllBookings();
  }

  /**
   * Get pending emergency bookings
   */
  getPendingBookings(): EmergencyBookingRequest[] {
    return this.cachedBookings.filter(b => b.status === 'pending' || b.status === 'approved');
  }

  /**
   * Get bookings for a specific client
   */
  getClientBookings(clientId: string): EmergencyBookingRequest[] {
    return this.cachedBookings.filter(b => b.clientId === clientId);
  }

  /**
   * Get staff notifications (derived from unseen bookings)
   * ✅ FIXED: No separate collection - derive from bookings
   */
  getStaffNotifications(): EmergencyNotification[] {
    return this.cachedBookings
      .filter(b => !this.seenBookingIds.has(b.id))
      .map(booking => ({
        id: `notif-${booking.id}`,
        type: 'emergency_booking' as const,
        bookingId: booking.id,
        clientName: booking.clientName,
        urgencyLevel: booking.urgencyLevel,
        createdAt: booking.createdAt,
        read: false
      }));
  }

  /**
   * Get unread notification count (unseen bookings)
   */
  getUnreadCount(): number {
    return this.cachedBookings.filter(b => !this.seenBookingIds.has(b.id)).length;
  }

  /**
   * Mark notification as read (mark booking as seen)
   * ✅ FIXED: Just tracks locally which bookings have been seen
   */
  async markNotificationAsRead(notificationId: string): Promise<void> {
    // Extract booking ID from notification ID (format: notif-{bookingId})
    const bookingId = notificationId.replace('notif-', '');
    this.seenBookingIds.add(bookingId);
    this.persistSeenToStorage(); // H4: survive refresh
    console.log('✓ Booking marked as seen:', bookingId);

    // Notify subscribers that notifications changed
    this.notifyNotificationSubscribers();
  }

  /**
   * Approve an emergency booking
   */
  async approveBooking(bookingId: string, data: {
    assignedBarber: string;
    assignedTime: Date;
    respondedBy: string;
  }): Promise<EmergencyBookingRequest | null> {
    try {
      await updateDoc(doc(db, this.BOOKINGS_COLLECTION, bookingId), {
        status: 'approved',
        assignedBarber: data.assignedBarber,
        assignedTime: Timestamp.fromDate(data.assignedTime),
        respondedBy: data.respondedBy,
        respondedAt: serverTimestamp()
      });

      const booking = this.cachedBookings.find(b => b.id === bookingId);
      if (booking) {
        booking.status = 'approved';
        booking.assignedBarber = data.assignedBarber;
        booking.assignedTime = data.assignedTime;
        booking.respondedBy = data.respondedBy;
        booking.respondedAt = new Date();
      }

      return booking || null;
    } catch (error) {
      console.error('Error approving booking:', error);
      return null;
    }
  }

  /**
   * Simple approve - updates status to approved
   */
  async approveBookingSimple(bookingId: string): Promise<void> {
    try {
      await updateDoc(doc(db, this.BOOKINGS_COLLECTION, bookingId), {
        status: 'approved',
        respondedAt: serverTimestamp()
      });
      console.log('✓ Booking approved');
    } catch (error) {
      console.error('Error approving booking:', error);
    }
  }

  /**
   * Reject an emergency booking
   */
  async rejectBooking(bookingId: string, data: {
    rejectionReason: string;
    respondedBy: string;
  }): Promise<EmergencyBookingRequest | null> {
    try {
      // Find alternative slots using AI
      const booking = this.cachedBookings.find(b => b.id === bookingId);
      const alternativeSlots = booking ? await this.findAlternativeSlots(booking) : [];

      await updateDoc(doc(db, this.BOOKINGS_COLLECTION, bookingId), {
        status: 'rejected',
        rejectionReason: data.rejectionReason,
        respondedBy: data.respondedBy,
        respondedAt: serverTimestamp(),
        alternativeSlots: alternativeSlots.map(slot => ({
          ...slot,
          date: Timestamp.fromDate(slot.date)
        }))
      });

      if (booking) {
        booking.status = 'rejected';
        booking.rejectionReason = data.rejectionReason;
        booking.respondedBy = data.respondedBy;
        booking.respondedAt = new Date();
        booking.alternativeSlots = alternativeSlots;
      }

      return booking || null;
    } catch (error) {
      console.error('Error rejecting booking:', error);
      return null;
    }
  }

  /**
   * Simple reject - updates status to rejected
   */
  async rejectBookingSimple(bookingId: string, reason: string): Promise<void> {
    try {
      await updateDoc(doc(db, this.BOOKINGS_COLLECTION, bookingId), {
        status: 'rejected',
        rejectionReason: reason,
        respondedAt: serverTimestamp()
      });
      console.log('✓ Booking rejected');
    } catch (error) {
      console.error('Error rejecting booking:', error);
    }
  }

  /**
   * Reject with alternative time proposal
   * Sets status to 'counter_offered' so client can accept or decline
   */
  async rejectWithAlternative(bookingId: string, alternative: {
    proposedDate: Date;
    proposedTime: string;
    proposedBarber: string;
    proposedBarberId?: string;
    message?: string;
  }): Promise<EmergencyBookingRequest | null> {
    try {
      const proposedAlternative = {
        date: Timestamp.fromDate(alternative.proposedDate),
        time: alternative.proposedTime,
        barber: alternative.proposedBarber,
        barberId: alternative.proposedBarberId || null,
        message: alternative.message || '',
        proposedAt: serverTimestamp()
      };

      await updateDoc(doc(db, this.BOOKINGS_COLLECTION, bookingId), {
        status: 'counter_offered',
        proposedAlternative,
        respondedAt: serverTimestamp()
      });

      // Update local cache
      const booking = this.cachedBookings.find(b => b.id === bookingId);
      if (booking) {
        booking.status = 'counter_offered';
        booking.proposedAlternative = {
          date: alternative.proposedDate,
          time: alternative.proposedTime,
          barber: alternative.proposedBarber,
          barberId: alternative.proposedBarberId,
          message: alternative.message,
          proposedAt: new Date()
        };
        booking.respondedAt = new Date();
      }

      console.log('✓ Counter offer sent to client:', alternative.proposedDate, alternative.proposedTime);

      // Send SMS notification if client has phone — routed through SLIK Connect's durable queue (source
      // 'emergency') so the counter-offer THREADS + is labeled, and the client's reply lands in the same
      // conversation. The processOutboundSms CF sends/suppresses. (Jose 2026-10-30)
      if (booking && booking.clientPhone) {
        const formattedDate = alternative.proposedDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        });

        // Format time to 12-hour
        const [hours, minutes] = alternative.proposedTime.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12;
        const formattedTime = `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;

        // Customer portal URL - use production URL or localhost for dev
        const dashboardUrl = process.env.NODE_ENV === 'production'
          ? `${PORTAL_URL}/dashboard`   // branded customer portal (my.); one source: src/config/urls.ts
          : 'http://localhost:3001/dashboard';

        const ok = await communicationService.sendToClient({
          clientId: (booking as any).clientId,
          clientName: booking.clientName,
          phone: booking.clientPhone,
          source: 'emergency',
          body: buildCounterOfferBody({
            clientName: booking.clientName.split(' ')[0], // First name only
            proposedDate: formattedDate,
            proposedTime: formattedTime,
            barberName: alternative.proposedBarber,
            message: alternative.message,
            dashboardUrl,
          }),
        });
        const smsResult = { success: ok, error: ok ? undefined : 'Could not queue the message' };

        if (smsResult.success) {
          console.log('SMS notification sent to client');
        } else {
          // Surface SMS failure so UI can show a warning to staff
          console.error('SMS notification failed:', smsResult.error);
          // Don't throw — the counter-offer was saved, just SMS failed
          // The booking update still stands, staff should be informed
          if (booking) {
            (booking as any).smsNotificationFailed = true;
            (booking as any).smsError = smsResult.error;
          }
        }
      }

      return booking || null;

    } catch (error) {
      console.error('Error sending counter offer:', error);
      return null;
    }
  }

  /**
   * Client accepts the proposed alternative time
   * Called from customer portal when client confirms the new slot
   */
  async acceptAlternative(bookingId: string): Promise<EmergencyBookingRequest | null> {
    try {
      const booking = this.cachedBookings.find(b => b.id === bookingId);
      if (!booking || !booking.proposedAlternative) {
        console.error('No proposed alternative found for booking:', bookingId);
        return null;
      }

      // Update to approved with the alternative as the assigned time
      await updateDoc(doc(db, this.BOOKINGS_COLLECTION, bookingId), {
        status: 'alternative_accepted',
        assignedBarber: booking.proposedAlternative.barber,
        assignedTime: Timestamp.fromDate(booking.proposedAlternative.date),
        respondedAt: serverTimestamp()
      });

      // Update local cache
      booking.status = 'alternative_accepted';
      booking.assignedBarber = booking.proposedAlternative.barber;
      booking.assignedTime = booking.proposedAlternative.date;

      console.log('✓ Client accepted alternative time');
      return booking;

    } catch (error) {
      console.error('Error accepting alternative:', error);
      return null;
    }
  }

  /**
   * Client declines the proposed alternative
   * Sets status back to rejected
   */
  async declineAlternative(bookingId: string): Promise<void> {
    try {
      await updateDoc(doc(db, this.BOOKINGS_COLLECTION, bookingId), {
        status: 'rejected',
        rejectionReason: 'Client declined proposed alternative time'
      });

      const booking = this.cachedBookings.find(b => b.id === bookingId);
      if (booking) {
        booking.status = 'rejected';
        booking.rejectionReason = 'Client declined proposed alternative time';
      }

      console.log('✓ Client declined alternative');
    } catch (error) {
      console.error('Error declining alternative:', error);
    }
  }

  /**
   * AI-powered alternative slot finder
   */
  private async findAlternativeSlots(booking: EmergencyBookingRequest): Promise<Array<{
    date: Date;
    time: string;
    barber: string;
    confidence: number;
  }>> {
    const alternatives: Array<{
      date: Date;
      time: string;
      barber: string;
      confidence: number;
    }> = [];

    const requestedDate = new Date(booking.requestedDate);

    // Suggest next day same time
    const nextDay = new Date(requestedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    alternatives.push({
      date: nextDay,
      time: this.getTimeSlotString(booking.requestedTimeSlot),
      barber: 'Next Available Barber',
      confidence: 95
    });

    // Suggest same day later time
    if (booking.requestedTimeSlot !== 'evening') {
      alternatives.push({
        date: requestedDate,
        time: 'Evening (3:00 PM - 6:00 PM)',
        barber: 'Any Available',
        confidence: 75
      });
    }

    // Suggest 2 days later
    const twoDaysLater = new Date(requestedDate);
    twoDaysLater.setDate(twoDaysLater.getDate() + 2);
    alternatives.push({
      date: twoDaysLater,
      time: this.getTimeSlotString(booking.requestedTimeSlot),
      barber: 'Premium Barber',
      confidence: 85
    });

    return alternatives;
  }

  private getTimeSlotString(slot: 'morning' | 'afternoon' | 'evening' | 'asap'): string {
    const slots = {
      morning: 'Morning (9:00 AM - 12:00 PM)',
      afternoon: 'Afternoon (12:00 PM - 3:00 PM)',
      evening: 'Evening (3:00 PM - 6:00 PM)',
      asap: 'ASAP (Next Available)'
    };
    return slots[slot];
  }

  /**
   * Get emergency booking statistics
   */
  getStats(): EmergencyBookingStats {
    const bookings = this.cachedBookings;
    const pending = bookings.filter(b => b.status === 'pending' || b.status === 'approved');

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayBookings = bookings.filter(b => {
      const bookingDate = new Date(b.createdAt);
      bookingDate.setHours(0, 0, 0, 0);
      return bookingDate.getTime() === today.getTime();
    });

    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekBookings = bookings.filter(b => new Date(b.createdAt) >= weekAgo);

    const approvedCount = bookings.filter(b => b.status === 'approved' || b.status === 'completed').length;
    const approvalRate = bookings.length > 0 ? (approvedCount / bookings.length) * 100 : 0;

    return {
      pendingCount: pending.length,
      todayCount: todayBookings.length,
      thisWeekCount: weekBookings.length,
      approvalRate: Math.round(approvalRate)
    };
  }

  /**
   * Subscribe to emergency booking updates (real-time via Firebase)
   */
  subscribe(callback: (bookings: EmergencyBookingRequest[]) => void): () => void {
    this.subscribers.push(callback);
    // Immediately call with current data
    callback(this.cachedBookings);

    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to notification updates
   * ✅ FIXED: Now uses derived notifications from bookings
   */
  subscribeToNotifications(callback: (notifications: EmergencyNotification[]) => void): () => void {
    this.notificationSubscribers.push(callback);
    // Immediately call with derived notifications
    callback(this.getStaffNotifications());

    return () => {
      this.notificationSubscribers = this.notificationSubscribers.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all booking subscribers
   */
  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback(this.cachedBookings));
  }

  /**
   * Clear all emergency bookings (development only)
   */
  async clearAll(): Promise<void> {
    try {
      const bookingsSnapshot = await getDocs(collection(db, this.BOOKINGS_COLLECTION));

      const deletePromises = bookingsSnapshot.docs.map(docSnap => deleteDoc(docSnap.ref));
      await Promise.all(deletePromises);

      // Also clear local seen tracking
      this.seenBookingIds.clear();
      this.persistSeenToStorage(); // H4: keep storage coherent

      console.log('✓ All emergency data cleared');
    } catch (error) {
      console.error('Error clearing data:', error);
    }
  }

  /**
   * Cleanup listeners on service destroy
   */
  destroy(): void {
    if (this.unsubscribeBookings) {
      this.unsubscribeBookings();
    }
  }

  /** Load persisted seen-ids (H4) — survives page refresh so dismissed emergencies don't re-pop. */
  private loadSeenFromStorage(): void {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(EmergencyBookingService.SEEN_STORAGE_KEY) : null;
      if (raw) { const arr = JSON.parse(raw); if (Array.isArray(arr)) this.seenBookingIds = new Set(arr); }
    } catch { /* ignore — fall back to empty set */ }
  }

  /** Persist seen-ids (H4). Capped so the list can't grow unbounded over time. */
  private persistSeenToStorage(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const arr = Array.from(this.seenBookingIds).slice(-300); // keep most-recent 300
      localStorage.setItem(EmergencyBookingService.SEEN_STORAGE_KEY, JSON.stringify(arr));
    } catch { /* ignore */ }
  }

  /**
   * Mark a booking as seen (for popup dismissal)
   */
  markBookingAsSeen(bookingId: string): void {
    this.seenBookingIds.add(bookingId);
    this.persistSeenToStorage();
    this.notifyNotificationSubscribers();
  }

  /**
   * Check if booking has been seen
   */
  isBookingSeen(bookingId: string): boolean {
    return this.seenBookingIds.has(bookingId);
  }

  /**
   * Force refresh from Firebase server (bypasses cache)
   */
  async forceRefresh(): Promise<void> {
    try {
      console.log('🔄 Forcing refresh from Firebase server (bypassing cache)...');
      const bookingsQuery = query(
        collection(db, this.BOOKINGS_COLLECTION),
        orderBy('createdAt', 'desc')
      );

      // Use getDocsFromServer to force fetch from server, not cache
      const snapshot = await getDocsFromServer(bookingsQuery);

      this.cachedBookings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        requestedDate: doc.data().requestedDate?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        respondedAt: doc.data().respondedAt?.toDate(),
        assignedTime: doc.data().assignedTime?.toDate(),
        alternativeSlots: doc.data().alternativeSlots?.map((slot: any) => ({
          ...slot,
          date: slot.date?.toDate() || new Date()
        }))
      })) as EmergencyBookingRequest[];

      console.log('✅ Refreshed', this.cachedBookings.length, 'bookings from server');
      console.log('📊 Pending bookings:', this.cachedBookings.filter(b => b.status === 'pending').length);

      // Clear seen bookings to show fresh notifications (explicit manual refresh = intentional).
      this.seenBookingIds.clear();
      this.persistSeenToStorage(); // H4: keep storage coherent with the in-memory set

      // Notify subscribers
      this.notifySubscribers();
      this.notifyNotificationSubscribers();
    } catch (error) {
      console.error('Error forcing refresh:', error);
    }
  }
}

export const emergencyBookingService = new EmergencyBookingService();
export type { EmergencyBookingRequest, EmergencyNotification, EmergencyBookingStats };
